import { cn } from "../lib/utils";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, useState, useCallback } from "react";

// ErrorBoundaryInner: minimal class component kept solely to catch render-phase errors,
// which cannot be caught by hooks. All UI logic is handled by the outer function component.
class ErrorBoundaryInner extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    this.props.onError(error);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

const ErrorBoundary = ({ children }) => {
  const [errorState, setErrorState] = useState({ hasError: false, error: null });

  const handleError = useCallback((error) => {
    setErrorState({ hasError: true, error });
  }, []);

  if (errorState.hasError) {
    return (
      <div className="flex items-center justify-center min-h-screen p-8 bg-background">
        <div className="flex flex-col items-center w-full max-w-2xl p-8">
          <AlertTriangle
            size={48}
            className="text-destructive mb-6 flex-shrink-0"
          />

          <h2 className="text-xl mb-4">An unexpected error occurred.</h2>

          <div className="p-4 w-full rounded bg-muted overflow-auto mb-6">
            <pre className="text-sm text-muted-foreground whitespace-break-spaces">
              {errorState.error?.stack}
            </pre>
          </div>

          <button
            onClick={() => window.location.reload()}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg",
              "bg-primary text-primary-foreground",
              "hover:opacity-90 cursor-pointer"
            )}
          >
            <RotateCcw size={16} />
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundaryInner onError={handleError}>
      {children}
    </ErrorBoundaryInner>
  );
};

export default ErrorBoundary;
